/**
 * Lectura centralizada de environment del admin (Vue CLI).
 *
 * Solo variables con prefijo VUE_APP_ llegan al cliente.
 * Se definen en .env / .env.development / .env.production
 * y se inyectan en build/serve vía process.env.
 *
 * Uso típico de flags:
 *   import { get, set, isProduction } from '@/utils/environment';
 *
 *   if (get('aplicarValidacionesEstrictas')) {
 *     // validar en prod / según config
 *   }
 *
 *   // override temporal (p. ej. pruebas en consola):
 *   set('aplicarValidacionesEstrictas', false);
 */

const ENV_ALIASES = {
  dev: 'development',
  local: 'development',
  qas: 'qas',
  qa: 'qas',
  uat: 'staging',
  stage: 'staging',
  prod: 'production',
};

/**
 * Flags / config por environment.
 * Cada clave puede tener valores por env + `default`.
 */
const ENV_FLAGS = {
  /** Validaciones estrictas de formularios / flujos de negocio */
  aplicarValidacionesEstrictas: {
    development: false,
    qas: false,
    staging: true,
    production: true,
    default: true,
  },
};

/** Overrides en runtime (set). No persisten entre recargas. */
const runtimeOverrides = {};

export function getAppEnv() {
  const raw = process.env.VUE_APP_ENV || process.env.NODE_ENV || 'development';
  return ENV_ALIASES[raw] || raw;
}

export function isEnv(...envs) {
  const current = getAppEnv();
  return envs.some((env) => {
    const normalized = ENV_ALIASES[env] || env;
    return normalized === current;
  });
}

export function isDevelopment() {
  return isEnv('development');
}

export function isQas() {
  return isEnv('qas');
}

export function isStaging() {
  return isEnv('staging');
}

export function isProduction() {
  return isEnv('production');
}

/** Ambientes no productivos (dev / qas). */
export function isNonProduction() {
  return isEnv('development', 'qas');
}

export function getEnvVar(name, fallback = undefined) {
  const key = name.startsWith('VUE_APP_') ? name : `VUE_APP_${name}`;
  const value = process.env[key];
  return value !== undefined && value !== '' ? value : fallback;
}

/**
 * Obtiene un flag/config según el environment actual.
 * Prioridad: override de set() → valor del env → default del flag → fallback.
 *
 * @param {string} key
 * @param {*} [fallback]
 */
export function get(key, fallback = undefined) {
  if (Object.prototype.hasOwnProperty.call(runtimeOverrides, key)) {
    return runtimeOverrides[key];
  }

  const flag = ENV_FLAGS[key];
  if (!flag || typeof flag !== 'object') {
    return fallback;
  }

  const env = getAppEnv();
  if (Object.prototype.hasOwnProperty.call(flag, env)) {
    return flag[env];
  }

  if (Object.prototype.hasOwnProperty.call(flag, 'default')) {
    return flag.default;
  }

  return fallback;
}

/**
 * Define un override en runtime para un flag/config.
 * Útil para pruebas o ajustar comportamiento sin redeploy.
 *
 * @param {string} key
 * @param {*} value
 */
export function set(key, value) {
  runtimeOverrides[key] = value;
  return value;
}

/**
 * Quita un override (o todos si no se pasa key).
 * @param {string} [key]
 */
export function clear(key) {
  if (key === undefined) {
    Object.keys(runtimeOverrides).forEach((k) => delete runtimeOverrides[k]);
    return;
  }
  delete runtimeOverrides[key];
}

/**
 * Registra o actualiza la definición base de un flag (por env).
 * @param {string} key
 * @param {Object} envMap - { development, qas, staging, production, default }
 */
export function define(key, envMap = {}) {
  ENV_FLAGS[key] = { ...(ENV_FLAGS[key] || {}), ...envMap };
}

/**
 * Atajo: ¿debe aplicarse la regla/validación?
 * Equivale a `!!get(key, true)` en prod-like, pero usa el flag tipado.
 */
export function shouldEnforce(key = 'aplicarValidacionesEstrictas') {
  return !!get(key, isProduction());
}
